import { TenantLocation } from "../../../domain/tenantLocations/tenantLocation"
import { AddJobsRecord, AddJobsWorkArea, Route, RouteJob } from "../routingApi"

export const DEFAULT_MAP_ZOOM = 10

export type Job = {
  jobId: number
  jobName: string
  customerId: number
  customerDisplayName: string
  address1: string
  city: string
  state: string
  zip: string
  workareaCount: number
  latitude?: number
  longitude?: number
  workAreas: AddJobsWorkArea[]
}

export const defaultAddRouteJobsFilters: AddRouteJobsFilters = {
  divisionIds: [],
  costCodeIds: [],
  projectTypeIds: [3],
  statusIds: [],
  salesPersonIds: [],
  searchTerm: '',
  jobsNotAddedToRoutes: true,
  showSelectedJobsOnly: false
}

export type AddRouteJobsFilters = {
  divisionIds: number[]
  costCodeIds: number[]
  projectTypeIds: number[]
  statusIds: number[]
  salesPersonIds: string[]
  searchTerm: string
  jobsNotAddedToRoutes: boolean
  showSelectedJobsOnly: boolean
}

export type ClientsJobsRecord = {
  customerId: number
  customerDisplayName: string
  jobs: AddJobsRecord[]
}

export type PropertyGeolocationUpdateRecord = {
  propertyId: number
  nickname: string
  address1: string
  address2: string | null
  city: string
  state: string
  zip: string
  county: string
  countryCode: string
  latitude: number
  longitude: number
}

export type Action =
  | { type: 'JOB_WORKAREAS_CHANGED', jobId: number, workAreaIds: number[] }
  | { type: 'JOB_REMOVED', jobId: number }
  | { type: 'JOB_ESTIMATED_DURATION_CHANGED', jobId: number, estimatedDurationInMinutes: number }

export const reducer = (state: RouteJob[], action: Action): RouteJob[] => {
  switch (action.type) {
    case 'JOB_WORKAREAS_CHANGED':
      return state.map(job => job.jobId === action.jobId ? { ...job, workAreaIds: action.workAreaIds.join(',') } : job)
    case 'JOB_REMOVED':
      return state
        .filter(job => job.jobId !== action.jobId)
        .map((job, index) => ({ ...job, sortIndex: index }))
    case 'JOB_ESTIMATED_DURATION_CHANGED':
      return state.map(job => job.jobId === action.jobId ? { ...job, estimatedDurationInMinutes: action.estimatedDurationInMinutes } : job)
    default:
      return state
  }
}

export const loadRouteJobs = (jobs: Job[], existingJobs?: RouteJob[]): RouteJob[] => {
  return jobs.map((job, index) => {
    const existing = existingJobs?.find(existingJob => existingJob.jobId === job.jobId)
    return {
      id: 0,
      jobId: job.jobId,
      routeId: 0,
      estimatedDurationInMinutes: existing?.estimatedDurationInMinutes ?? 15,
      workAreaIds: existing?.workAreaIds || '',
      tenantId: 0,
      tenantLocationId: null,
      sortIndex: index
    }
  })
}

export const buildFinalRouteJobs = (
  routeJobs: RouteJob[],
  routeId: number,
  startingLocation?: RouteJob | null,
  endingLocation?: RouteJob | null,
  shopLocation?: TenantLocation | null
): RouteJob[] => {
  let finalJobs = [...routeJobs]

  if (!startingLocation) {
    finalJobs.unshift({
      id: 0,
      routeId: routeId!,
      jobId: null,
      tenantLocationId: shopLocation?.id ?? null,
      estimatedDurationInMinutes: 0,
      workAreaIds: '',
      tenantId: 0,
      sortIndex: 0
    })
  } else {
    const startingLocationIndex = startingLocation.jobId ? finalJobs.findIndex(job => job.jobId === startingLocation.jobId) : finalJobs.findIndex(job => job.tenantLocationId === startingLocation.tenantLocationId)
    if (startingLocationIndex !== -1) {
      finalJobs.splice(startingLocationIndex, 1)
      finalJobs.unshift({
        ...startingLocation,
        routeId: routeId!,
        sortIndex: 0
      })
    } else {
      finalJobs.unshift({
        ...startingLocation,
        routeId: routeId!,
        sortIndex: 0
      })
    }
  }

  if (!endingLocation) {
    finalJobs.push({
      id: 0,
      routeId: routeId!,
      jobId: null,
      tenantLocationId: shopLocation?.id ?? null,
      estimatedDurationInMinutes: 0,
      workAreaIds: '',
      tenantId: 0,
      sortIndex: finalJobs.length
    })
  } else {
    if (endingLocation.tenantLocationId !== null) {
      finalJobs.push({
        ...endingLocation,
        routeId: routeId!,
        sortIndex: finalJobs.length
      })
    } else {
      const endingLocationIndex = finalJobs.findIndex(job => job.jobId === endingLocation.jobId)
      if (endingLocationIndex !== -1 && endingLocationIndex !== 0) {
        finalJobs.splice(endingLocationIndex, 1)
        finalJobs.push({
          ...endingLocation,
          routeId: routeId!,
          sortIndex: finalJobs.length
        })
      } else {
        finalJobs.push({
          ...endingLocation,
          routeId: routeId!,
          sortIndex: finalJobs.length
        })
      }
    }
  }

  finalJobs = finalJobs.map((job, index) => ({
    ...job,
    sortIndex: index
  }))


  return finalJobs
}

export const getClientsJobsRecords = (jobs: AddJobsRecord[]): ClientsJobsRecord[] => {
  const jobsByCustomer = new Map<number, { customerDisplayName: string, jobs: AddJobsRecord[] }>()

  jobs.forEach(job => {
    const customerId = job.customerId
    if (!jobsByCustomer.has(customerId)) {
      jobsByCustomer.set(customerId, {
        customerDisplayName: job.customerDisplayName,
        jobs: []
      })
    }
    jobsByCustomer.get(customerId)?.jobs.push(job)
  })

  return Array.from(jobsByCustomer.entries()).map(([customerId, data]) => ({
    customerId,
    customerDisplayName: data.customerDisplayName,
    jobs: data.jobs
  }))
}

export const getRouteJobIds = (routeJobs: RouteJob[] | undefined): number[] => {
  return (routeJobs || [])
    .filter(routeJob => routeJob.jobId !== null)
    .map(routeJob => routeJob.jobId!)
}

export const parseWorkAreaIds = (workAreaIds: string | null | undefined): number[] => {
  return (workAreaIds || '')
    .split(',')
    .map(id => parseInt(id.trim()))
    .filter(id => !isNaN(id))
}

export const buildRouteJobRecord = (routeJob: RouteJob): AddJobsRecord | null => {
  if (routeJob.jobId === null || routeJob.jobId === undefined || !routeJob.job) {
    return null
  }

  const workAreaIds = parseWorkAreaIds(routeJob.workAreaIds)

  return {
    jobId: routeJob.jobId,
    jobName: routeJob.job.jobName,
    customerId: 0,
    customerDisplayName: routeJob.job.customerDisplayName,
    workareaCount: workAreaIds.length,
    active: true,
    allDivisionIds: '',
    type: '',
    status: '',
    salesPersonEmail: '',
    address1: routeJob.job.address1,
    address2: routeJob.job.address2,
    city: routeJob.job.city,
    state: routeJob.job.state,
    zip: routeJob.job.zip,
    county: null,
    countryCode: null,
    nickname: null,
    propertyId: 0,
    latitude: routeJob.job.latitude,
    longitude: routeJob.job.longitude,
    workAreas: workAreaIds.map(id => ({ id, divisionId: null, isApproved: true })),
    isSold: false
  }
}

export const withRouteJobsIncluded = (
  jobs: AddJobsRecord[] | undefined,
  routeJobs: RouteJob[] | undefined
): AddJobsRecord[] | undefined => {
  if (!jobs) {
    return jobs
  }

  const knownJobIds = new Set(jobs.map(job => job.jobId))
  const additions: AddJobsRecord[] = []

  for (const routeJob of routeJobs || []) {
    if (routeJob.jobId === null || knownJobIds.has(routeJob.jobId)) {
      continue
    }
    const record = buildRouteJobRecord(routeJob)
    if (record) {
      knownJobIds.add(record.jobId)
      additions.push(record)
    }
  }

  if (additions.length === 0) {
    return jobs
  }

  return [...jobs, ...additions]
}

export const getFilteredJobs = (jobs: AddJobsRecord[], filters: AddRouteJobsFilters, selectedJobIds: number[] = [], routeJobIds: number[] = []): AddJobsRecord[] => {
  const filteredJobs = jobs
    .filter(job => {
      const isOnRoute = routeJobIds.includes(job.jobId)

      const approvedWorkAreaDivisionIds = job.workAreas
        .filter(wa => wa.isApproved && wa.divisionId != null)
        .map(wa => wa.divisionId!)
      const matchesDivision = isOnRoute || filters.divisionIds.length === 0 ||
        approvedWorkAreaDivisionIds.some(id => filters.divisionIds.includes(id))

      const matchesProjectType = isOnRoute || filters.projectTypeIds.length === 0 ||
        filters.projectTypeIds.includes(parseInt(job.type))

      const matchesStatus = isOnRoute || filters.statusIds.length === 0 ||
        filters.statusIds.includes(parseInt(job.status))

      const matchesSalesPerson = isOnRoute || filters.salesPersonIds.length === 0 ||
        filters.salesPersonIds.includes(job.salesPersonEmail)

      const searchLower = filters.searchTerm.toLowerCase()
      const matchesSearch = job.customerDisplayName.toLowerCase().includes(searchLower) ||
        job.jobName.toLowerCase().includes(searchLower) ||
        (job.address1 && job.address1.toLowerCase().includes(searchLower)) ||
        (job.city && job.city.toLowerCase().includes(searchLower)) ||
        (job.state && job.state.toLowerCase().includes(searchLower)) ||
        (job.zip && job.zip.toLowerCase().includes(searchLower))

      const matchesSelectedJobs = !filters.showSelectedJobsOnly || selectedJobIds.includes(job.jobId)

      return matchesDivision && matchesProjectType && matchesStatus && matchesSalesPerson && matchesSearch && matchesSelectedJobs
    })

  return filteredJobs
}

export const hasJobError = (job: Job): boolean => {
  return !job.address1 || !job.city || !job.state || !job.zip || !job.latitude || !job.longitude
}

export const hasNoWorkAreas = (job: Job): boolean => {
  return job.workareaCount === 0
}

export const hasNoApprovedWorkAreas = (job: Job): boolean => {
  return !job.workAreas.some(wa => wa.isApproved)
}

export const isJobSelectable = (job: AddJobsRecord): boolean => {
  return !hasJobError(job) && !hasNoWorkAreas(job) && !hasNoApprovedWorkAreas(job)
}

export const getSelectableFilteredJobIds = (
  jobs: AddJobsRecord[],
  filters: AddRouteJobsFilters,
  selectedJobIds: number[]
): number[] => {
  return getFilteredJobs(jobs, filters, selectedJobIds)
    .filter(isJobSelectable)
    .map(job => job.jobId)
}

export const getUnroutedJobIds = (
  jobIds: number[],
  jobRoutesMap: Map<number, Route[]>
): number[] => {
  return jobIds.filter(id => !jobRoutesMap.has(id))
}

export const getSelectableUnroutedJobIds = (
  jobs: AddJobsRecord[],
  filters: AddRouteJobsFilters,
  selectedJobIds: number[],
  jobRoutesMap: Map<number, Route[]>
): number[] => {
  const candidates = getSelectableFilteredJobIds(
    jobs,
    { ...filters, showSelectedJobsOnly: false },
    selectedJobIds
  )
  return getUnroutedJobIds(candidates, jobRoutesMap)
}

export const areAllJobsSelected = (
  selectableJobIds: number[],
  selectedJobIds: number[]
): boolean => {
  if (selectableJobIds.length === 0) {
    return false
  }
  const selectedSet = new Set(selectedJobIds)
  return selectableJobIds.every(id => selectedSet.has(id))
}
